import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useIsMobile } from '@/hooks/use-mobile';
import { useApiClient } from '@/lib/api/hooks';
import { QUICK_PICKS, type QuickPick } from './quick-picks.const';
import { loadRecents, type Recent } from './recents';

export type StylePick = {
  style: string;
  brand: string;
  styleName: string;
  title?: string;
};

type StyleComboboxProps = {
  value: string;
  onPick: (pick: StylePick) => void;
};

export function StyleCombobox({ value, onPick }: StyleComboboxProps) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();

  function handlePick(pick: StylePick) {
    onPick(pick);
    setOpen(false);
  }

  const trigger = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      className="h-11 w-full justify-between text-base font-normal"
      onClick={() => setOpen(true)}
    >
      <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
        {value || 'Search or pick a style'}
      </span>
      <ChevronsUpDown className="ml-2 size-4 shrink-0 text-muted-foreground" />
    </Button>
  );

  return (
    <>
      {isMobile ? (
        <>
          {trigger}
          <CommandDialog open={open} onOpenChange={setOpen} title="Pick a style">
            <StylePickerBody currentValue={value} onPick={handlePick} />
          </CommandDialog>
        </>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
            <Command shouldFilter={false}>
              <StylePickerBody currentValue={value} onPick={handlePick} />
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
}

function StylePickerBody({
  currentValue,
  onPick,
}: {
  currentValue: string;
  onPick: (pick: StylePick) => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const client = useApiClient();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const search = useQuery({
    queryKey: ['style-search', debounced],
    enabled: debounced.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await client.GET('/garments/styles/search', {
        params: { query: { q: debounced, limit: 20 } },
      });
      if (error) throw error;
      return data.data.results;
    },
  });

  const [recents, setRecents] = useState<Recent[]>([]);
  useEffect(() => {
    setRecents(loadRecents());
  }, []);

  const showSuggestions = debounced.length === 0;

  return (
    <>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search brand, style, or part number…"
        autoFocus
      />
      <CommandList className="max-h-[360px]">
        {showSuggestions ? (
          <>
            <CommandGroup heading="Quick picks">
              {QUICK_PICKS.map((pick) => (
                <QuickPickRow
                  key={pick.style}
                  pick={pick}
                  isActive={pick.style === currentValue}
                  onSelect={() => onPick(quickToPick(pick))}
                />
              ))}
            </CommandGroup>
            {recents.length > 0 && (
              <CommandGroup heading="Recents">
                {recents.map((r) => (
                  <CommandItem
                    key={`${r.style}|${r.colorName}`}
                    value={`${r.style}|${r.colorName}`}
                    onSelect={() => {
                      onPick({
                        style: r.style,
                        brand: r.style.split(' ')[0] ?? r.style,
                        styleName: r.style.split(' ').slice(1).join(' ') || r.style,
                      });
                    }}
                  >
                    <div className="flex flex-1 items-center justify-between gap-3">
                      <span className="truncate text-sm">{r.style}</span>
                      <span className="text-xs text-muted-foreground">{r.colorName}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </>
        ) : search.isLoading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Searching…
          </div>
        ) : search.isError ? (
          <div className="px-4 py-6 text-sm text-destructive">
            Search unavailable. Type a style manually (e.g. "Gildan 5000") — it'll still quote.
          </div>
        ) : !search.data || search.data.length === 0 ? (
          <CommandEmpty>
            <div className="space-y-1 px-2 py-4 text-center">
              <p className="text-sm">No styles match "{debounced}"</p>
              <p className="text-xs text-muted-foreground">
                Try the brand + style number (e.g. "Gildan 5000") or a title keyword.
              </p>
            </div>
          </CommandEmpty>
        ) : (
          <CommandGroup
            heading={`${search.data.length} match${search.data.length === 1 ? '' : 'es'}`}
          >
            {search.data.map((result) => (
              <CommandItem
                key={result.styleId}
                value={`${result.brand}-${result.styleName}-${result.styleId}`}
                onSelect={() =>
                  onPick({
                    style: `${result.brand} ${result.styleName}`,
                    brand: result.brand,
                    styleName: result.styleName,
                    title: result.title,
                  })
                }
              >
                <ResultRow
                  result={result}
                  isActive={`${result.brand} ${result.styleName}` === currentValue}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </>
  );
}

function QuickPickRow({
  pick,
  isActive,
  onSelect,
}: {
  pick: QuickPick;
  isActive: boolean;
  onSelect: () => void;
}) {
  return (
    <CommandItem value={`quick-${pick.style}`} onSelect={onSelect}>
      <div className="flex flex-1 items-center gap-3">
        <span className="font-mono text-sm font-semibold">{pick.label}</span>
        <span className="text-xs text-muted-foreground">— {pick.subtitle}</span>
      </div>
      {isActive && <Check className="ml-2 size-4 text-primary" />}
    </CommandItem>
  );
}

function ResultRow({
  result,
  isActive,
}: {
  result: {
    brand: string;
    styleName: string;
    title?: string;
    basePrice?: number | null;
    partNumber?: string;
  };
  isActive: boolean;
}) {
  return (
    <div className="flex flex-1 items-center gap-3">
      <Badge
        variant="secondary"
        className="shrink-0 text-[10px] font-medium uppercase tracking-wider"
      >
        {result.brand}
      </Badge>
      <span className="font-semibold">{result.styleName}</span>
      {result.title && (
        <span className="truncate text-xs text-muted-foreground">· {result.title}</span>
      )}
      {typeof result.basePrice === 'number' && (
        <span className="ml-auto shrink-0 text-xs text-muted-foreground tabular-nums">
          ${result.basePrice.toFixed(2)}
        </span>
      )}
      {isActive && <Check className="ml-2 size-4 text-primary" />}
    </div>
  );
}

function quickToPick(pick: QuickPick): StylePick {
  const parts = pick.style.split(' ');
  const brand = parts[0] ?? pick.style;
  const styleName = parts.slice(1).join(' ') || pick.label;
  return {
    style: pick.style,
    brand,
    styleName,
    title: pick.subtitle,
  };
}

// Suppress unused-import warning when Search lucide icon was considered for
// the empty state but not used in the final layout. Keeping the import slot
// empty cleaner than conditional imports.
void Search;
